/**
 * Fase 7 — leads (formulários instantâneos): formulários da página, leads recebidos pelo
 * webhook `leadgen`, destinos de envio (webhook, e-mail, Google Sheets, WhatsApp), retenção
 * (LGPD) e a normalização pura dos campos do lead.
 */
import { z } from "zod";

/** Tipos de pergunta padrão da Meta (`questions[].type`) que o produto expõe. */
export const LEAD_QUESTION_TYPES = [
  "FULL_NAME",
  "FIRST_NAME",
  "LAST_NAME",
  "EMAIL",
  "PHONE",
  "CITY",
  "STATE",
  "ZIP",
  "COMPANY_NAME",
  "JOB_TITLE",
  "CUSTOM",
] as const;
export type LeadQuestionType = (typeof LEAD_QUESTION_TYPES)[number];

export const LEAD_QUESTION_LABELS: Record<LeadQuestionType, string> = {
  FULL_NAME: "Nome completo",
  FIRST_NAME: "Primeiro nome",
  LAST_NAME: "Sobrenome",
  EMAIL: "E-mail",
  PHONE: "Telefone",
  CITY: "Cidade",
  STATE: "Estado",
  ZIP: "CEP",
  COMPANY_NAME: "Empresa",
  JOB_TITLE: "Cargo",
  CUSTOM: "Pergunta personalizada",
};

export const LEAD_STATUSES = ["novo", "contatado", "qualificado", "convertido", "descartado"] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  novo: "Novo",
  contatado: "Contatado",
  qualificado: "Qualificado",
  convertido: "Convertido",
  descartado: "Descartado",
};

export const LEAD_DESTINATION_KINDS = ["webhook", "email", "google_sheets", "whatsapp"] as const;
export type LeadDestinationKind = (typeof LEAD_DESTINATION_KINDS)[number];

export const LEAD_DESTINATION_LABELS: Record<LeadDestinationKind, string> = {
  webhook: "Webhook do cliente",
  email: "E-mail",
  google_sheets: "Google Sheets",
  whatsapp: "WhatsApp (API do provedor)",
};

/** Permissões opcionais da Meta que o módulo de leads precisa (documentadas no README). */
export const META_LEADS_SCOPES = ["leads_retrieval", "pages_manage_metadata"] as const;

// ---- DTOs ---------------------------------------------------------------------------------------

export interface LeadFormQuestionDto {
  key: string;
  label: string;
  type: string;
  options?: string[];
}

export interface LeadFormDto {
  id: string;
  metaId: string;
  pageMetaId: string;
  pageName: string | null;
  clientId: string | null;
  name: string;
  status: string;
  locale: string | null;
  questions: LeadFormQuestionDto[];
  leadsCount: number | null;
  /** Leads já gravados no IAGENIX. */
  localLeads: number;
  privacyPolicyUrl: string | null;
  metaCreatedAt: string | null;
  syncedAt: string;
}

export interface LeadPageDto {
  metaId: string;
  name: string;
  clientId: string | null;
  /** Assinatura `leadgen` (`/{page_id}/subscribed_apps`) feita por este app. */
  leadgenSubscribedAt: string | null;
  leadgenSubscriptionError: string | null;
  /** A conexão que vê a página tem as permissões `leads_retrieval` + `pages_manage_metadata`? */
  scopesOk: boolean;
  missingScopes: string[];
}

export interface LeadDeliveryDto {
  id: string;
  destinationId: string;
  destinationName: string;
  kind: LeadDestinationKind;
  status: "pendente" | "ok" | "erro";
  attempts: number;
  error: string | null;
  responseStatus: number | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

export interface LeadDto {
  id: string;
  metaId: string;
  formMetaId: string;
  formName: string | null;
  pageMetaId: string;
  pageName: string | null;
  clientId: string | null;
  adMetaId: string | null;
  adName: string | null;
  adsetMetaId: string | null;
  campaignMetaId: string | null;
  campaignName: string | null;
  platform: string | null;
  isOrganic: boolean;
  name: string | null;
  email: string | null;
  phone: string | null;
  /** Link direto para abrir conversa no WhatsApp (quando há telefone). */
  whatsappUrl: string | null;
  fields: Record<string, string>;
  status: LeadStatus;
  notes: string;
  /** `webhook` (tempo real) ou `backfill` (busca manual). */
  source: "webhook" | "backfill" | "simulado";
  metaCreatedAt: string;
  receivedAt: string;
  deliveries: LeadDeliveryDto[];
}

export interface LeadListDto {
  items: LeadDto[];
  total: number;
  nextCursor: string | null;
}

export interface LeadDestinationDto {
  id: string;
  kind: LeadDestinationKind;
  name: string;
  clientId: string | null;
  formMetaId: string | null;
  active: boolean;
  /** Configuração sem segredos (segredo/token nunca voltam). */
  config: Record<string, unknown>;
  hasSecret: boolean;
  lastDeliveryAt: string | null;
  lastDeliveryStatus: "ok" | "erro" | "pendente" | null;
  createdAt: string;
}

export interface LeadsSettingsDto {
  /** Dias de retenção dos leads (LGPD). Depois disso o job apaga. */
  retentionDays: number;
}

export interface LeadsOverviewDto {
  pages: LeadPageDto[];
  forms: LeadFormDto[];
  settings: LeadsSettingsDto;
  /** Dica quando a Meta ainda não está configurada para webhooks (modo live). */
  webhookConfigured: boolean;
}

// ---- Schemas ------------------------------------------------------------------------------------

const metaId = z.string().trim().regex(/^\d{1,30}$/, "Informe um id numérico da Meta.");

export const leadFormQuestionSchema = z
  .object({
    type: z.enum(LEAD_QUESTION_TYPES),
    /** Só em CUSTOM. */
    key: z
      .string()
      .trim()
      .regex(/^[a-z0-9_]{1,60}$/, "Use letras minúsculas, números e _.")
      .optional(),
    label: z.string().trim().min(1).max(120).optional(),
    /** Só em CUSTOM, para múltipla escolha. */
    options: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
  })
  .refine((q) => q.type !== "CUSTOM" || (q.key && q.label), {
    message: "Pergunta personalizada precisa de chave e rótulo.",
    path: ["label"],
  });

export const createLeadFormSchema = z.object({
  pageMetaId: metaId,
  name: z.string().trim().min(2, "Dê um nome ao formulário.").max(120),
  locale: z.string().trim().min(2).max(10).default("pt_BR"),
  questions: z.array(leadFormQuestionSchema).min(1, "Adicione pelo menos uma pergunta.").max(15),
  privacyPolicyUrl: z.url("Informe a URL da política de privacidade."),
  privacyLinkText: z.string().trim().max(80).optional(),
  thankYou: z.object({
    title: z.string().trim().min(1, "Informe o título.").max(80),
    body: z.string().trim().min(1, "Informe a mensagem.").max(600),
    buttonText: z.string().trim().min(1).max(60).default("Ver site"),
    buttonUrl: z.url("Informe a URL do botão."),
  }),
});
export type CreateLeadFormInput = z.input<typeof createLeadFormSchema>;

export const leadsQuerySchema = z.object({
  clientId: z.uuid().optional(),
  formMetaId: metaId.optional(),
  campaignMetaId: metaId.optional(),
  status: z.enum(LEAD_STATUSES).optional(),
  q: z.string().trim().max(120).optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  cursor: z.string().optional(),
});
export type LeadsQuery = z.infer<typeof leadsQuerySchema>;

export const updateLeadSchema = z.object({
  status: z.enum(LEAD_STATUSES).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;

export const leadsSettingsSchema = z.object({
  retentionDays: z.number().int().min(30, "Mínimo de 30 dias.").max(3650, "Máximo de 10 anos."),
});
export type LeadsSettingsInput = z.infer<typeof leadsSettingsSchema>;

const url = z.url("Informe uma URL válida.").max(2048);

export const leadDestinationConfigSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("webhook"),
    url,
    /** Assinatura HMAC-SHA256 (gerado se vazio). */
    secret: z.string().trim().min(16).max(200).optional(),
  }),
  z.object({
    kind: z.literal("email"),
    emails: z.array(z.email("Informe e-mails válidos.")).min(1, "Informe pelo menos um e-mail.").max(10),
  }),
  z.object({
    kind: z.literal("google_sheets"),
    /** URL do Web App do Apps Script (termina em /exec). */
    url: url.refine((u) => u.includes("script.google.com"), {
      message: "Use a URL do Web App do Apps Script (script.google.com/.../exec).",
    }),
    secret: z.string().trim().min(16).max(200).optional(),
  }),
  z.object({
    kind: z.literal("whatsapp"),
    /** Endpoint do provedor (Z-API, Evolution, Twilio, …) que recebe `{ phone, message }`. */
    url,
    authHeader: z.string().trim().max(80).optional(),
    authValue: z.string().trim().max(500).optional(),
    /** Mensagem com variáveis {nome}, {email}, {telefone}, {formulario}, {campanha}. */
    messageTemplate: z
      .string()
      .trim()
      .min(1)
      .max(1000)
      .default("Olá {nome}! Recebemos seu contato pelo formulário {formulario}. Em breve falaremos com você."),
  }),
]);
export type LeadDestinationConfigInput = z.input<typeof leadDestinationConfigSchema>;

export const createLeadDestinationSchema = z.object({
  name: z.string().trim().min(2, "Dê um nome ao destino.").max(80),
  clientId: z.uuid().nullable().default(null),
  formMetaId: metaId.nullable().default(null),
  active: z.boolean().default(true),
  config: leadDestinationConfigSchema,
});
export type CreateLeadDestinationInput = z.input<typeof createLeadDestinationSchema>;

export const updateLeadDestinationSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  clientId: z.uuid().nullable().optional(),
  formMetaId: metaId.nullable().optional(),
  active: z.boolean().optional(),
  config: leadDestinationConfigSchema.optional(),
});
export type UpdateLeadDestinationInput = z.infer<typeof updateLeadDestinationSchema>;

export const backfillLeadsSchema = z.object({
  formMetaId: metaId,
  days: z.number().int().min(1).max(90).default(90),
});

// ---- Normalização (puro) ------------------------------------------------------------------------

export interface MetaLeadFieldData {
  name: string;
  values: string[];
}

export interface NormalizedLead {
  name: string | null;
  email: string | null;
  phone: string | null;
  fields: Record<string, string>;
}

/** Telefone em E.164 para o Brasil quando possível (`+55DDDNÚMERO`); senão só os dígitos com `+`. */
export function normalizePhoneBr(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 8) return null;
  if (raw.trim().startsWith("+") && !digits.startsWith("55")) return `+${digits}`;
  let d = digits;
  if (d.startsWith("0055")) d = d.slice(4);
  else if (d.startsWith("55") && d.length >= 12) d = d.slice(2);
  else if (d.startsWith("0") && (d.length === 11 || d.length === 12)) d = d.slice(1);
  if (d.length === 10 || d.length === 11) return `+55${d}`;
  return `+${digits}`;
}

export function whatsappUrlFor(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 8 ? `https://wa.me/${digits}` : null;
}

const NAME_KEYS = ["full_name", "nome", "nome_completo", "name"];
const FIRST_KEYS = ["first_name", "primeiro_nome"];
const LAST_KEYS = ["last_name", "sobrenome"];
const EMAIL_KEYS = ["email", "e-mail", "work_email"];
const PHONE_KEYS = ["phone_number", "phone", "telefone", "celular", "whatsapp", "work_phone_number"];

/** Transforma o `field_data` da Meta em nome/e-mail/telefone + todos os campos. */
export function normalizeLead(fieldData: MetaLeadFieldData[] | null | undefined): NormalizedLead {
  const fields: Record<string, string> = {};
  let name: string | null = null;
  let first: string | null = null;
  let last: string | null = null;
  let email: string | null = null;
  let phone: string | null = null;
  for (const f of fieldData ?? []) {
    const key = f.name.trim().toLowerCase();
    const value = f.values.map((v) => v.trim()).filter(Boolean).join(", ");
    if (!value) continue;
    fields[f.name] = value;
    if (NAME_KEYS.includes(key) && !name) name = value;
    else if (FIRST_KEYS.includes(key)) first = value;
    else if (LAST_KEYS.includes(key)) last = value;
    else if (EMAIL_KEYS.includes(key) && !email) email = value.toLowerCase();
    else if (PHONE_KEYS.includes(key) && !phone) phone = normalizePhoneBr(value);
  }
  if (!name && (first || last)) name = [first, last].filter(Boolean).join(" ");
  return { name, email, phone, fields };
}

/** Substitui {nome}, {email}, {telefone}, {formulario}, {campanha} na mensagem do WhatsApp. */
export function renderLeadTemplate(
  template: string,
  lead: { name: string | null; email: string | null; phone: string | null; formName: string | null; campaignName: string | null },
): string {
  return template
    .replaceAll("{nome}", lead.name ?? "")
    .replaceAll("{email}", lead.email ?? "")
    .replaceAll("{telefone}", lead.phone ?? "")
    .replaceAll("{formulario}", lead.formName ?? "")
    .replaceAll("{campanha}", lead.campaignName ?? "");
}
